/**
 * Embed Settings Section
 * 
 * Displays Looker user attributes configuration for per-business data filtering
 * and planned future features.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Tune as TuneIcon,
  Palette as PaletteIcon,
  ViewQuilt as ViewQuiltIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { configApi, type LookerUserAttribute } from '../../services/api';

interface PlannedFeature {
  title: string;
  description: string;
  details: string[];
  icon: React.ReactNode;
}

const plannedFeatures: PlannedFeature[] = [
  {
    title: 'Default Filter Values',
    description:
      'Per-user configurable filter presets that get applied at embed time. When combined with hidden filters, admins can lock dashboards to specific data views per user.',
    details: [
      'Store per-user filter configs in DynamoDB',
      'Apply at embed time via Embed SDK .withFilters() method',
      'Support filter values as URL parameters on the embed URL',
      'Filters persist across dashboard refresh when bar is hidden',
    ],
    icon: <TuneIcon />,
  },
  {
    title: 'Per-Dashboard Settings',
    description:
      'Extend embed settings to allow different configurations per dashboard, not just per user. Enable dashboard-specific themes and filter presets.',
    details: [
      'Dashboard-level override for embed theme',
      'Dashboard-specific default filter values',
      'Per-dashboard visibility controls for UI elements',
      'Combine with user-level settings for fine-grained control',
    ],
    icon: <ViewQuiltIcon />,
  },
];

export function EmbedSettingsSection() {
  // Fetch user attributes from Looker
  const {
    data: userAttributes = [],
    isLoading,
    isError,
    error,
  } = useQuery<LookerUserAttribute[]>({
    queryKey: ['user-attributes'],
    queryFn: configApi.getUserAttributes,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Embed Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configure how dashboards are displayed and filtered for embedded viewers
        </Typography>
      </Box>

      {/* User Attributes Section - Active Feature */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
          <Typography variant="h6" fontWeight={600}>
            User Attributes (Data Filtering)
          </Typography>
          <Chip label="Active" size="small" color="success" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          User attributes control which data each embedded user can see. Values are set per-user
          in the Users section and automatically passed to Looker during embed sessions. Looker
          uses these values with access filters defined in LookML to enforce row-level data
          segmentation (e.g., showing only a specific business&apos;s data).
        </Typography>

        {/* User Attributes Table */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not fetch user attributes from Looker.{' '}
            {error instanceof Error ? error.message : 'Check your Looker API configuration.'}
          </Alert>
        ) : userAttributes.length > 0 ? (
          <>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Available Looker User Attributes
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Label</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Attribute Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Default Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userAttributes.map((attr) => (
                    <TableRow key={attr.id}>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{attr.label}</TableCell>
                      <TableCell>
                        <Box
                          component="code"
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            bgcolor: 'grey.100',
                            borderRadius: 0.5,
                            fontSize: '0.8rem',
                            fontFamily: 'monospace',
                          }}
                        >
                          {attr.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={attr.type}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 22 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', color: attr.default_value ? 'text.primary' : 'text.disabled' }}>
                        {attr.default_value || 'None'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            No custom user attributes are defined in your Looker instance. To enable per-business
            data filtering, create a user attribute (e.g., &quot;brand&quot;) in Looker Admin &gt; User Attributes.
          </Alert>
        )}
      </Paper>

      {/* Per-User Themes Section - Active Feature */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <PaletteIcon sx={{ color: 'success.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Per-User Themes
          </Typography>
          <Chip label="Active" size="small" color="success" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Looker themes control the visual appearance of embedded dashboards, including whether the
          filter bar is visible. Themes are assigned per-user in the <strong>Users</strong> section.
          Create themes in Looker Admin → Themes, then assign them to individual users.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderColor: 'grey.200' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            How to Create a Looker Theme
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.5,
              '& li': { mb: 0.75, lineHeight: 1.5, fontSize: '0.8rem', color: 'text.secondary' },
            }}
          >
            <Typography component="li" variant="body2">
              Go to <strong>Looker Admin → Themes</strong>
            </Typography>
            <Typography component="li" variant="body2">
              Click <strong>New Theme</strong> and name it (e.g., <code>partner_hidden_filters</code>)
            </Typography>
            <Typography component="li" variant="body2">
              Under <strong>Dashboard</strong> settings, configure:
              <Box component="ul" sx={{ mt: 0.5, pl: 2 }}>
                <Typography component="li" variant="caption" sx={{ display: 'block' }}>
                  <code>Show Filters Bar</code>: <strong>false</strong> (hides the filter bar)
                </Typography>
                <Typography component="li" variant="caption" sx={{ display: 'block' }}>
                  <code>Show Dashboard Menu</code>: <strong>false</strong> (optional - hides menu)
                </Typography>
                <Typography component="li" variant="caption" sx={{ display: 'block' }}>
                  <code>Show Reload Data Icon</code>: <strong>false</strong> (optional)
                </Typography>
              </Box>
            </Typography>
            <Typography component="li" variant="body2">
              Save the theme, then assign it to users in the <strong>Users</strong> section
            </Typography>
          </Box>
        </Paper>
      </Paper>

      {/* Planned Features */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Planned Features
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {plannedFeatures.map((feature) => (
          <Paper key={feature.title} variant="outlined" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {feature.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {feature.title}
                  </Typography>
                  <Chip label="Planned" size="small" variant="outlined" color="info" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.5,
                    '& li': {
                      mb: 0.5,
                    },
                  }}
                >
                  {feature.details.map((detail) => (
                    <Typography component="li" variant="caption" color="text.secondary" key={detail}>
                      {detail}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </>
  );
}

export default EmbedSettingsSection;
